import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { StompConfig, StompHeaders, StompRService } from '@stomp/ng2-stompjs';
import { Observable, map, switchMap } from 'rxjs';
import { EvalRescueComment } from '@app-model/eval-rescue';
import { SessionService } from '@app/store/session/session.service';
import { UserInfo } from '@app/store/session/session.store';


@Injectable({
  providedIn: 'root'
})
export class StompService {

  private subscriptions: Record<string, Observable<any>> = {};
  private initialazing: boolean = false;

  constructor(
    private stompService: StompRService,
    private sessionService: SessionService
  ) {
  }

  dealerTopic(userInfo: UserInfo): Observable<LiveBidItem> {
    return this.getAuthHeaders().pipe(
      switchMap(headers => {
        let dealerId = userInfo.dealerId;

        const dealerSelected = this.sessionService.getDealerSelected();
        if (dealerSelected) {
          dealerId = dealerSelected.id;
          headers['X-DealerId'] = dealerSelected.id;
        }

        return this.doSubscribe(headers, '/topic/dealer.liveBid.' + dealerId, dealerId);
      })
    );
  }

  vehicleTopic(vehicleId: string): Observable<LiveBidItem> {
    return this.getAuthHeaders().pipe(
      switchMap(headers => this.doSubscribe(headers, '/topic/vehicle.liveBid.' + vehicleId, vehicleId))
    );
  }

  evalVehicleTopic(evalVehicleId: string): Observable<LiveBidItem> {
    return this.getAuthHeaders().pipe(
      switchMap(headers => this.doSubscribe(headers, '/topic/eval.liveBid.' + evalVehicleId, evalVehicleId))
    );
  }

  fastQuoteTopic(fastQuoteId: string): Observable<LiveBidItem> {
    return this.getAuthHeaders().pipe(
      switchMap(headers => this.doSubscribe(headers, '/topic/fastQuote.liveBid.' + fastQuoteId, fastQuoteId))
    );
  }

  rescueTopic(rescueId: string): Observable<LiveRescueItem> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.doSubscribe<LiveRescueItem>(
          headers,
          '/topic/rescue.liveBid.' + rescueId,
          'rescue:' + rescueId,
        ),
      ),
    );
  }

  connected$(): Observable<void> {
    return this.stompService.connected$.pipe(map(() => undefined));
  }

  deleteSubscription(id: string): void {
    if (this.subscriptions[id]) {
      delete this.subscriptions[id];
    }
  }

  disconnect(): void {
    this.subscriptions = {};
    this.stompService.deactivate();
    this.stompService.disconnect();
  }

  private getAuthHeaders(): Observable<StompHeaders> {
    return this.sessionService.getAuthToken().pipe(
      map(jwt => ({ Authorization: `Bearer ${jwt}` } as StompHeaders))
    );
  }

  private doSubscribe<T = LiveBidItem>(headers: any, path: string, id: string): Observable<T> {
    this.stompService.config = {
      url: environment.webSocket,
      heartbeat_in: 0,
      heartbeat_out: 300000,
      reconnect_delay: 60000,
      headers: headers
    } as StompConfig;
    if (this.subscriptions[id]) {
      return this.subscriptions[id];
    }

    if (this.initialazing) {
        throw new Error("initialazing");
    }

    if (!this.stompService.active || !this.stompService.connected()) {
      this.initialazing = true;
      try {
        this.stompService.initAndConnect();
        setTimeout(() => {       
          this.initialazing = false
          }, 1000);
      } catch (error) {
        this.disconnect();
        throw new Error("error");
      }
    }
    const subscription = this.stompService.subscribe(path).pipe(
      map((resp) => {
        const decoder = new TextDecoder('utf-8');
        const msg = decoder.decode(new Uint8Array(resp.binaryBody));
        return JSON.parse(msg) as T;
      })
    );
    this.subscriptions[id] = subscription;
    return subscription;
  }
}

export class LiveBidItem {
  event: string;
  vehicleId: string;
  fastQuoteId: string;
  evalVehicleId: string;
  dealerIds: string[];
  actionUserId: string;
  details: VehicleStompDetails;
}

export class VehicleStompDetails {
  currentPrice: number;
  currentBidDealerId: string;
  currentBidDealerName: string;
}

export interface LiveRescueItem {
  event: string;
  rescueId: string;
  actionUserId: string;
  rescueDetails: RescueLiveDetails;
  latestComment?: RescueLatestComment | null;
}

export interface RescueLiveDetails {
  id: string;
  evalVehicleId: string;
  status: string;
  makeName?: string | null;
  modelName?: string | null;
  trimName?: string | null;
  manufactureYear?: number | null;
  modelYear?: number | null;
  numberPlate?: string | null;
  km?: number | null;
  fipePrice?: number | null;
  pricedValue?: number | null;
  fipePercentage?: number | null;
  oemDealerId?: string | null;
  oemDealerName?: string | null;
  evalDealerId?: string | null;
  evalDealerName?: string | null;
  city?: string | null;
  uf?: string | null;
  imgUrl?: string | null;
  observationsCount?: number | null;
  previousRescuesCount?: number | null;
  contactHidden?: boolean | null;
  availableToAssume?: boolean | null;
  assigneeUserId?: string | null;
  assigneeUserName?: string | null;
  assumedAt?: string | number | null;
  inProgressReason?: string | null;
  notRescuedReason?: string | null;
  createdAt?: string | number | null;
  updatedAt?: string | number | null;
  pricingEndedAt?: string | number | null;
}

export type RescueLatestComment = EvalRescueComment;
